import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { useCompany } from "../../components/context/CompanyContext";
import IndicadoresNav from "../../components/IndicadoresNav";
import Loading from "../../components/loading/loading";
import { titleSetRef } from "../../redux/titleRedux";
import { userRequest } from "../../requestMethods";
import { formatPricing, formatTimeDDMMYYY, retunrMonth } from "../../util/helper";
import { effectiveStories } from "../../util/storeScope";
import "../../App.css";

const BRAND = "#EE8253";

const STATUS_CONFIG = {
  Pendente:   { bg: "#f1d1c3", color: "#863c1c" },
  Confirmado: { bg: "#f7e0f3", color: "#a3298f" },
  Pago:       { bg: "#c2e0f4", color: "#0e67a3" },
  Preparado:  { bg: "#bdc2e9", color: "#040a3e" },
  Finalizado: { bg: "#c7f6da", color: "#06632b" },
  Cancelado:  { bg: "rgb(243,142,142)", color: "#c00" },
};

function KpiCard({ icon, title, heroValue, sub, gradient, onClick, cta }) {
  const [hover, setHover] = useState(false);
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => onClick && setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        background: `linear-gradient(135deg, ${gradient[0]}, ${gradient[1]})`,
        borderRadius: 16,
        overflow: "hidden",
        cursor: onClick ? "pointer" : "default",
        transition: "transform 0.18s, box-shadow 0.18s",
        transform: hover ? "translateY(-3px)" : "none",
        boxShadow: hover ? "0 10px 28px rgba(0,0,0,0.20)" : "0 3px 10px rgba(0,0,0,0.10)",
        position: "relative",
      }}
    >
      {/* Blobs decorativos */}
      <div style={{ position: "absolute", right: -24, top: -24, width: 110, height: 110, borderRadius: "50%", background: "rgba(255,255,255,0.10)", pointerEvents: "none" }} />
      <div style={{ position: "absolute", right: 16, bottom: -32, width: 80, height: 80, borderRadius: "50%", background: "rgba(255,255,255,0.06)", pointerEvents: "none" }} />

      {/* Corpo */}
      <div style={{ padding: "18px 20px 14px", position: "relative" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 }}>
          <span style={{ fontSize: 10, fontWeight: 800, color: "rgba(255,255,255,0.80)", textTransform: "uppercase", letterSpacing: "0.09em", fontFamily: "Manrope" }}>{title}</span>
          {icon && (
            <div style={{ width: 34, height: 34, borderRadius: 10, background: "rgba(255,255,255,0.20)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              {icon}
            </div>
          )}
        </div>
        <div style={{ fontSize: 26, fontWeight: 900, color: "#fff", fontFamily: "Manrope", letterSpacing: "-0.02em", lineHeight: 1.1 }}>{heroValue}</div>
        <div style={{ fontSize: 11, color: "rgba(255,255,255,0.70)", fontFamily: "Manrope", marginTop: 5 }}>{sub}</div>
      </div>

      {/* Rodapé CTA */}
      <div style={{ borderTop: "1px solid rgba(255,255,255,0.15)", padding: "9px 20px", display: "flex", alignItems: "center", justifyContent: "space-between", background: "rgba(0,0,0,0.10)" }}>
        <span style={{ fontSize: 11, color: "rgba(255,255,255,0.85)", fontFamily: "Manrope", fontWeight: 600 }}>{cta || "Ver detalhes"}</span>
        <span style={{ fontSize: 14, color: "rgba(255,255,255,0.65)" }}>›</span>
      </div>
    </div>
  );
}

function StatusCard({ label, count, value, onClick }) {
  const [hover, setHover] = useState(false);
  const cfg = STATUS_CONFIG[label] || { bg: "#F3F4F6", color: "#374151" };
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        background: cfg.bg,
        border: `1.5px solid ${cfg.color}33`,
        borderRadius: 12,
        padding: "14px 16px",
        cursor: "pointer",
        transition: "transform 0.15s, box-shadow 0.15s",
        transform: hover ? "translateY(-2px)" : "none",
        boxShadow: hover ? "0 4px 12px rgba(0,0,0,0.12)" : "0 1px 4px rgba(0,0,0,0.06)",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: cfg.color, fontFamily: "Manrope" }}>{label}</span>
        <OpenInNewIcon style={{ fontSize: 13, color: cfg.color, opacity: 0.6 }} />
      </div>
      <div style={{ fontSize: 18, fontWeight: 900, color: cfg.color, fontFamily: "Manrope" }}>{formatPricing(value)}</div>
      <div style={{ fontSize: 11, color: cfg.color, opacity: 0.7, fontFamily: "Manrope", marginTop: 2 }}>{count} pedido{count !== 1 ? "s" : ""}</div>
    </div>
  );
}

function ChartCard({ title, sub, children, badge }) {
  return (
    <div style={{
      background: "#fff",
      borderRadius: 14,
      border: "1px solid #E5E7EB",
      boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
      padding: "18px 18px 14px",
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#111827", fontFamily: "Manrope" }}>{title}</div>
          {sub && <div style={{ fontSize: 11, color: "#9CA3AF", fontFamily: "Manrope", marginTop: 2 }}>{sub}</div>}
        </div>
        {badge && (
          <span style={{ fontSize: 11, fontWeight: 700, color: BRAND, background: `${BRAND}18`, borderRadius: 20, padding: "3px 10px", fontFamily: "Manrope" }}>
            {badge}
          </span>
        )}
      </div>
      {children}
    </div>
  );
}

function EmptyChart({ height = 260 }) {
  return (
    <div style={{ height, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", color: "#D1D5DB" }}>
      <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="3" y="12" width="4" height="9" rx="1"/><rect x="10" y="7" width="4" height="14" rx="1"/><rect x="17" y="3" width="4" height="18" rx="1"/></svg>
      <span style={{ fontSize: 12, fontFamily: "Manrope", marginTop: 8 }}>Sem dados no período</span>
    </div>
  );
}

function CustomTooltip({ active, payload, label, formatter }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: 10, padding: "10px 14px", boxShadow: "0 4px 16px rgba(0,0,0,0.10)", fontFamily: "Manrope" }}>
      {label && <div style={{ fontSize: 11, color: "#6B7280", marginBottom: 6, fontWeight: 600 }}>{label}</div>}
      {payload.map((entry, i) => (
        <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12 }}>
          <span style={{ width: 10, height: 10, borderRadius: 3, background: entry.color, display: "inline-block", flexShrink: 0 }} />
          <span style={{ color: "#374151" }}>{entry.name || "Total"}: </span>
          <span style={{ fontWeight: 700, color: "#111827" }}>
            {formatter ? formatter(entry.value) : entry.value}
          </span>
        </div>
      ))}
    </div>
  );
}

function CartBucketCard({ label, count, value, onClick }) {
  const [hover, setHover] = useState(false);
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => onClick && setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        background: "#fff",
        border: "1px solid #E5E7EB",
        borderRadius: 12,
        padding: "16px 18px",
        textAlign: "center",
        cursor: onClick ? "pointer" : "default",
        transition: "transform 0.15s, box-shadow 0.15s",
        transform: hover ? "translateY(-2px)" : "none",
        boxShadow: hover ? "0 4px 12px rgba(0,0,0,0.10)" : "0 1px 4px rgba(0,0,0,0.05)",
      }}
    >
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 4 }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: "#6B7280", fontFamily: "Manrope", textTransform: "uppercase", letterSpacing: "0.06em" }}>{label}</span>
        {onClick && <OpenInNewIcon style={{ fontSize: 12, color: "#9CA3AF" }} />}
      </div>
      <div style={{ fontSize: 20, fontWeight: 900, color: "#111827", fontFamily: "Manrope", margin: "8px 0 4px" }}>{formatPricing(value)}</div>
      <div style={{ fontSize: 11, color: "#9CA3AF", fontFamily: "Manrope" }}>{count} carrinho{count !== 1 ? "s" : ""}</div>
    </div>
  );
}

export default function IndicadoresVendas() {
  const [orders, setOrders] = useState(null);
  const [carts, setCarts] = useState(null);
  const [ordersDay, setOrdersDay] = useState([]);
  const [ordersMonth, setOrdersMonth] = useState([]);
  const [avgOrdersDay, setAvgOrdersDay] = useState([]);
  const [ordersStore, setOrdersStore] = useState([]);
  const [avgOrdersStore, setAvgOrdersStore] = useState([]);
  const [ordersUser, setOrdersUser] = useState([]);
  const [avgOrdersUser, setAvgOrdersUser] = useState([]);
  const [ordersRep, setOrdersRep] = useState([]);
  const [avgOrdersRep, setAvgOrdersRep] = useState([]);
  const [reps, setReps] = useState([]);
  const [stories, setStories] = useState([]);
  const [selected, setSelected] = useState([]);
  const [storeIds, setStoreIds] = useState([]);
  const [dataIni, setDateIni] = useState(() => dayjs().add(-7, "day").format("YYYY-MM-DD"));
  const [dataFim, setDateFim] = useState(() => dayjs().format("YYYY-MM-DD"));
  const [readonly, setReadonly] = useState("7");
  const [isLoading, setIsLoading] = useState(true);

  const currentUser = useSelector((state) => state.user.currentUser);
  const { companyId, slug } = useCompany();
  const nav = useNavigate();
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(titleSetRef({ title: "Indicadores Vendas" }));
    if (!currentUser) nav("../login");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (companyId > 0 && currentUser) {
      getStories();
      getUserRep();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  useEffect(() => {
    setStoreIds(selected.map((item) => item.value));
  }, [selected]);

  useEffect(() => {
    if (companyId > 0 && currentUser) {
      setIsLoading(true);
      carregarTudo();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, dataIni, dataFim, storeIds]);

  const buildObj = () => ({
    start: dataIni,
    end: dataFim,
    stories: effectiveStories(currentUser, storeIds),
  });

  async function carregarTudo() {
    try {
      await Promise.all([
        getOrders(),
        getCarts(),
        getOrdersbyDay(),
        getOrdersbyMonth(),
        getAvgOrdersbyDay(),
        getOrdersbyStore(),
        getAvgOrdersbyStore(),
        getOrdersbyUser(),
        getAvgOrdersbyUser(),
        getOrdersbyRep(),
        getAvgOrdersbyRep(),
      ]);
    } finally {
      setIsLoading(false);
    }
  }

  const getStories = async () => {
    try {
      const res = await userRequest.get(`${companyId}/store/stories`);
      const list = res.data.map((item) => ({ label: item.store, value: item.storeId }));
      setStories(list);
      if (currentUser?.position === "Gerente") {
        const userStore = list.find((s) => s.value === currentUser.storeId);
        if (userStore) setSelected([userStore]);
      }
    } catch {}
  };

  const getUserRep = async () => {
    try {
      const res = await userRequest.get(`${companyId}/usersRep`);
      setReps(res.data || []);
    } catch {}
  };

  const getOrders = async () => {
    try {
      const res = await userRequest.post(`${companyId}/orders/find`, {
        page: 0,
        limit: 10000,
        paginated: true,
        filters: [
          { field: "createdAt", operator: "greaterThanOrEqual", value: dataIni },
          { field: "createdAt", operator: "lessThanOrEqual", value: dataFim },
        ],
        stories: effectiveStories(currentUser, storeIds),
      });
      const list = res.data.rows || [];
      const byStatus = (s) => list.filter((o) => o.status === s);
      const sum = (arr) => arr.reduce((acc, o) => acc + (o.valorLiquido || 0), 0);
      setOrders({
        cTotal:      list.length,       aTotal:      sum(list),
        cPendente:   byStatus("Pendente").length,   aPendente:   sum(byStatus("Pendente")),
        cConfirm:    byStatus("Confirmado").length, aConfirm:    sum(byStatus("Confirmado")),
        cPago:       byStatus("Pago").length,       aPago:       sum(byStatus("Pago")),
        cPreparado:  byStatus("Preparado").length,  aPreparado:  sum(byStatus("Preparado")),
        cFinalizado: byStatus("Finalizado").length, aFinalizado: sum(byStatus("Finalizado")),
        cCancelado:  byStatus("Cancelado").length,  aCancelado:  sum(byStatus("Cancelado")),
      });
    } catch {}
  };

  const getCarts = async () => {
    try {
      const res = await userRequest.post(`${companyId}/reportCarts/cartsTime`, { stories: effectiveStories(currentUser, storeIds) });
      setCarts(res.data);
    } catch {}
  };

  const getOrdersbyDay = async () => {
    try {
      const obj = { ...buildObj() };
      if (!currentUser?.isAdmin) obj.userId = currentUser?.userId;
      const res = await userRequest.post(`${companyId}/reportOrders/ordersday`, obj);
      setOrdersDay(res.data.map((o) => ({ criado: formatTimeDDMMYYY(o.createdAt), Total: o.amount })));
    } catch {}
  };

  const getOrdersbyMonth = async () => {
    try {
      const res = await userRequest.post(`${companyId}/reportOrders/ordersmonth`, { stories: effectiveStories(currentUser, storeIds) });
      setOrdersMonth(res.data.map((o) => ({ criado: retunrMonth(o.createdAt), Total: o.amount })));
    } catch {}
  };

  const getAvgOrdersbyDay = async () => {
    try {
      const res = await userRequest.post(`${companyId}/reportOrders/avgordersday`, { stories: effectiveStories(currentUser, storeIds) });
      setAvgOrdersDay(res.data.map((o) => ({ criado: retunrMonth(o.createdAt), Total: o.amount })));
    } catch {}
  };

  const getOrdersbyStore = async () => {
    try {
      const res = await userRequest.post(`${companyId}/reportOrders/ordersstore`, buildObj());
      setOrdersStore(res.data.map((o) => ({ Total: o.total, storeName: o.storeName })));
    } catch {}
  };

  const getAvgOrdersbyStore = async () => {
    try {
      const res = await userRequest.post(`${companyId}/reportOrders/avgordersstore`, buildObj());
      setAvgOrdersStore(res.data.map((o) => ({ Total: o.total, storeName: o.storeName })));
    } catch {}
  };

  const getOrdersbyUser = async () => {
    try {
      const res = await userRequest.post(`${companyId}/reportOrders/ordersuser`, buildObj());
      setOrdersUser(res.data.map((o) => ({ Total: o.total, managedBy: o.managedBy })));
    } catch {}
  };

  const getAvgOrdersbyUser = async () => {
    try {
      const res = await userRequest.post(`${companyId}/reportOrders/avgordersuser`, buildObj());
      setAvgOrdersUser(res.data.map((o) => ({ Total: o.total, managedBy: o.managedBy })));
    } catch {}
  };

  const getOrdersbyRep = async () => {
    try {
      const res = await userRequest.post(`${companyId}/reportOrders/orderrep`, buildObj());
      setOrdersRep(res.data.map((o) => ({ Total: o.total, userRepName: o.userRepName })));
    } catch {}
  };

  const getAvgOrdersbyRep = async () => {
    try {
      const res = await userRequest.post(`${companyId}/reportOrders/avgordersrep`, buildObj());
      setAvgOrdersRep(res.data.map((o) => ({ Total: o.total, userRepName: o.userRepName })));
    } catch {}
  };

  const handleChange = (e) => {
    const v = e.target.value;
    setReadonly(v);
    switch (v) {
      case "0": setDateIni(dayjs().format("YYYY-MM-DD")); setDateFim(dayjs().format("YYYY-MM-DD")); break;
      case "7": setDateIni(dayjs().add(-7, "day").format("YYYY-MM-DD")); setDateFim(dayjs().format("YYYY-MM-DD")); break;
      case "15": setDateIni(dayjs().add(-15, "day").format("YYYY-MM-DD")); setDateFim(dayjs().format("YYYY-MM-DD")); break;
      case "30": setDateIni(dayjs().add(-30, "day").format("YYYY-MM-DD")); setDateFim(dayjs().format("YYYY-MM-DD")); break;
      default: break;
    }
  };

  const periodLabel =
    readonly === "F" ? "filtro personalizado" : readonly === "0" ? "hoje" : `últimos ${readonly} dias`;

  const storeQS = storeIds.length > 0 ? `&storeId=${storeIds.join(",")}` : "";

  const brlAxis = (tick) => tick.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
  const brlTooltip = (v) => v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

  const ticketMedio =
    (orders?.cFinalizado || 0) > 0 ? (orders.aFinalizado || 0) / orders.cFinalizado : 0;

  const statusItems = [
    { label: "Pendente",   count: orders?.cPendente   || 0, value: orders?.aPendente   || 0 },
    { label: "Confirmado", count: orders?.cConfirm    || 0, value: orders?.aConfirm    || 0 },
    { label: "Pago",       count: orders?.cPago       || 0, value: orders?.aPago       || 0 },
    { label: "Preparado",  count: orders?.cPreparado  || 0, value: orders?.aPreparado  || 0 },
    { label: "Finalizado", count: orders?.cFinalizado || 0, value: orders?.aFinalizado || 0 },
    { label: "Cancelado",  count: orders?.cCancelado  || 0, value: orders?.aCancelado  || 0 },
  ];

  return (
    <div style={{ padding: "16px 16px 48px 4px", fontFamily: "Manrope", background: "#F9FAFB", minHeight: "100vh" }}>

      <IndicadoresNav />

      {/* Header + filtros */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20, justifyContent: "space-between" }}>
        <div>
          <span style={{ fontSize: 18, fontWeight: 700, color: "#111827" }}>Indicadores Vendas</span>
          <span style={{ fontSize: 12, color: "#9CA3AF", marginLeft: 8 }}>{periodLabel}</span>
        </div>
        <div style={{ display: "flex", alignItems: "flex-end", gap: 12 }}>

          {/* Retângulo Lojas */}
          {stories.length > 0 && (() => {
            const isGerente = currentUser?.position === "Gerente";
            return (
              <div>
                <div style={{ fontSize: 10, fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 4 }}>Lojas</div>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap", border: "1px solid #E5E7EB", borderRadius: 8, padding: "6px 8px", background: "#fff" }}>
                  <label
                    style={{
                      cursor: isGerente ? "default" : "pointer",
                      padding: "3px 10px",
                      borderRadius: 6,
                      border: `1px solid ${selected.length === 0 ? BRAND : "#E5E7EB"}`,
                      background: selected.length === 0 ? BRAND : "transparent",
                      color: selected.length === 0 ? "#fff" : "#6B7280",
                      fontSize: 12,
                      fontWeight: 600,
                    }}
                    onClick={() => !isGerente && setSelected([])}
                  >
                    Todas
                  </label>
                  {stories.map((story) => {
                    const isActive = selected.some((s) => s.value === story.value);
                    return (
                      <label
                        key={story.value}
                        style={{
                          cursor: isGerente ? "default" : "pointer",
                          padding: "3px 10px",
                          borderRadius: 6,
                          border: `1px solid ${isActive ? BRAND : "#E5E7EB"}`,
                          background: isActive ? BRAND : "transparent",
                          color: isActive ? "#fff" : "#6B7280",
                          fontSize: 12,
                          fontWeight: 600,
                          opacity: isGerente ? 0.7 : 1,
                        }}
                        onClick={() => {
                          if (isGerente) return;
                          setSelected(isActive
                            ? selected.filter((s) => s.value !== story.value)
                            : [...selected, story]
                          );
                        }}
                      >
                        {story.label}
                      </label>
                    );
                  })}
                </div>
              </div>
            );
          })()}

          {/* Retângulo Período */}
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 4 }}>Período</div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", border: "1px solid #E5E7EB", borderRadius: 8, padding: "6px 8px", background: "#fff" }}>
              {[
                { v: "30", l: "30 dias" },
                { v: "15", l: "15 dias" },
                { v: "7",  l: "7 dias"  },
                { v: "0",  l: "Hoje"    },
                { v: "F",  l: "Filtro"  },
              ].map(({ v, l }) => (
                <label
                  key={v}
                  style={{
                    cursor: "pointer",
                    padding: "3px 10px",
                    borderRadius: 6,
                    border: `1px solid ${readonly === v ? BRAND : "#E5E7EB"}`,
                    background: readonly === v ? BRAND : "transparent",
                    color: readonly === v ? "#fff" : "#6B7280",
                    fontSize: 12,
                    fontWeight: 600,
                  }}
                >
                  <input type="radio" value={v} checked={readonly === v} onChange={handleChange} style={{ display: "none" }} />
                  {l}
                </label>
              ))}
              {readonly === "F" && (
                <>
                  <div style={{ width: 1, background: "#E5E7EB", margin: "2px 2px" }} />
                  <input type="date" value={dataIni} onChange={(e) => setDateIni(e.target.value)} min="2023-01-01" style={{ fontSize: 12, border: "none", outline: "none" }} />
                  <span style={{ color: "#9CA3AF", fontSize: 12 }}>—</span>
                  <input type="date" value={dataFim} onChange={(e) => setDateFim(e.target.value)} min="2023-01-01" style={{ fontSize: 12, border: "none", outline: "none" }} />
                </>
              )}
            </div>
          </div>

        </div>
      </div>

      {isLoading ? (
        <Loading />
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>

          {/* KPIs hero */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16 }}>
            <KpiCard
              icon={<TrendingUpIcon style={{ fontSize: 20, color: "#fff" }} />}
              title="Faturamento"
              heroValue={formatPricing(orders?.aFinalizado || 0)}
              sub={`${orders?.cFinalizado || 0} pedidos finalizados`}
              gradient={["#EE8253", "#E8502A"]}
              cta="Ver pedidos finalizados"
              onClick={() => nav(`/${slug}/orders?start=${dataIni}&end=${dataFim}&status=Finalizado${storeQS}&from=indicadoresvendas`)}
            />
            <KpiCard
              title="Total Pedidos"
              heroValue={orders?.cTotal || 0}
              sub="pedidos no período"
              gradient={["#10B981", "#059669"]}
              cta="Ver todos os pedidos"
              onClick={() => nav(`/${slug}/orders?start=${dataIni}&end=${dataFim}${storeQS}&from=indicadoresvendas`)}
            />
            <KpiCard
              title="Ticket Médio"
              heroValue={formatPricing(ticketMedio)}
              sub="por pedido finalizado"
              gradient={["#3B82F6", "#1D4ED8"]}
              cta="Ver pedidos finalizados"
              onClick={() => nav(`/${slug}/orders?start=${dataIni}&end=${dataFim}&status=Finalizado${storeQS}&from=indicadoresvendas`)}
            />
            <KpiCard
              icon={<ShoppingCartIcon style={{ fontSize: 20, color: "#fff" }} />}
              title="Carrinhos"
              heroValue={formatPricing(carts?.total || 0)}
              sub={`${carts?.quantityTotal || 0} carrinhos abertos`}
              gradient={["#6B7280", "#374151"]}
              cta="Ver carrinhos"
              onClick={() => nav(`/${slug}/cartlist?from=indicadoresvendas`)}
            />
          </div>

          {/* Status dos Pedidos */}
          <div style={{
            background: "#fff",
            borderRadius: 14,
            border: "1px solid #E5E7EB",
            boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
            padding: "18px 20px 16px",
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
              <span style={{ fontSize: 14, fontWeight: 700, color: "#111827", fontFamily: "Manrope" }}>Status dos Pedidos</span>
              <span
                onClick={() => nav(`/${slug}/orders?start=${dataIni}&end=${dataFim}${storeQS}&from=indicadoresvendas`)}
                style={{ fontSize: 12, color: BRAND, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", gap: 4 }}
              >
                Ver todos <OpenInNewIcon style={{ fontSize: 13 }} />
              </span>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: 12 }}>
              {statusItems.map(({ label, count, value }) => (
                <StatusCard
                  key={label}
                  label={label}
                  count={count}
                  value={value}
                  onClick={() => nav(`/${slug}/orders?start=${dataIni}&end=${dataFim}&status=${label}${storeQS}&from=indicadoresvendas`)}
                />
              ))}
            </div>
          </div>

          {/* Carrinhos — 4 buckets */}
          <div style={{
            background: "#fff",
            borderRadius: 14,
            border: "1px solid #E5E7EB",
            boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
            padding: "18px 20px 16px",
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
              <span style={{ fontSize: 14, fontWeight: 700, color: "#111827", fontFamily: "Manrope" }}>Carrinhos em Aberto</span>
              <span
                onClick={() => nav(`/${slug}/cartlist?from=indicadoresvendas`)}
                style={{ fontSize: 12, color: BRAND, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", gap: 4 }}
              >
                Ver carrinho <OpenInNewIcon style={{ fontSize: 13 }} />
              </span>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12 }}>
              <CartBucketCard label="Total" count={carts?.quantityTotal || 0} value={carts?.total || 0} onClick={() => nav(`/${slug}/cartlist?from=indicadoresvendas`)} />
              <CartBucketCard label="Ativos (24h)" count={carts?.quantityAtual || 0} value={carts?.totalAtual || 0} onClick={() => nav(`/${slug}/cartlist?time=ativos&from=indicadoresvendas`)} />
              <CartBucketCard label="Paralisados (24h–48h)" count={carts?.quantityEntre || 0} value={carts?.totalEntre || 0} onClick={() => nav(`/${slug}/cartlist?time=paralizados&from=indicadoresvendas`)} />
              <CartBucketCard label="Inativos (+48h)" count={carts?.quantityMais || 0} value={carts?.totalMais || 0} onClick={() => nav(`/${slug}/cartlist?time=inativos&from=indicadoresvendas`)} />
            </div>
          </div>

          {/* Gráficos linha 1: Evolução diária + Histórico mensal */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
            <ChartCard
              title="Evolução Diária de Vendas"
              sub={periodLabel}
              badge={ordersDay.length ? `${ordersDay.length} dias` : null}
            >
              {ordersDay.length === 0 ? <EmptyChart /> : (
                <ResponsiveContainer width="100%" height={260}>
                  <AreaChart data={ordersDay} margin={{ top: 8, right: 16, left: 10, bottom: 0 }}>
                    <defs>
                      <linearGradient id="gradBrand" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={BRAND} stopOpacity={0.25} />
                        <stop offset="95%" stopColor={BRAND} stopOpacity={0.02} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid stroke="#F3F4F6" strokeDasharray="4 4" vertical={false} />
                    <XAxis dataKey="criado" tick={{ fontSize: 11, fill: "#9CA3AF" }} axisLine={false} tickLine={false} />
                    <YAxis width={88} tick={{ fontSize: 11, fill: "#9CA3AF" }} tickFormatter={brlAxis} axisLine={false} tickLine={false} />
                    <Tooltip content={<CustomTooltip formatter={brlTooltip} />} />
                    {ordersDay.length > 1 && (
                      <ReferenceLine
                        y={ordersDay.reduce((s, o) => s + o.Total, 0) / ordersDay.length}
                        stroke="#94A3B8"
                        strokeDasharray="5 3"
                        label={{ value: "Média", position: "insideTopRight", fontSize: 10, fill: "#94A3B8" }}
                      />
                    )}
                    <Area type="monotone" dataKey="Total" name="Vendas" stroke={BRAND} strokeWidth={2} fill="url(#gradBrand)" dot={false} activeDot={{ r: 5, fill: BRAND }} />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </ChartCard>
            <ChartCard
              title="Histórico Mensal de Vendas"
              sub="do primeiro dia do ano até hoje"
              badge={ordersMonth.length ? `${ordersMonth.length} meses` : null}
            >
              {ordersMonth.length === 0 ? <EmptyChart /> : (
                <ResponsiveContainer width="100%" height={260}>
                  <LineChart data={ordersMonth} margin={{ top: 16, right: 24, left: 10, bottom: 0 }}>
                    <defs>
                      <filter id="shadowLine" x="-20%" y="-20%" width="140%" height="140%">
                        <feDropShadow dx="0" dy="3" stdDeviation="4" floodColor={BRAND} floodOpacity="0.25" />
                      </filter>
                    </defs>
                    <CartesianGrid stroke="#F3F4F6" strokeDasharray="4 4" vertical={false} />
                    <XAxis dataKey="criado" tick={{ fontSize: 11, fill: "#9CA3AF" }} axisLine={false} tickLine={false} />
                    <YAxis width={88} tick={{ fontSize: 11, fill: "#9CA3AF" }} tickFormatter={brlAxis} axisLine={false} tickLine={false} />
                    <Tooltip content={<CustomTooltip formatter={brlTooltip} />} />
                    <Line
                      type="monotone"
                      dataKey="Total"
                      name="Vendas"
                      stroke={BRAND}
                      strokeWidth={3}
                      dot={{ r: 5, fill: "#fff", stroke: BRAND, strokeWidth: 2.5 }}
                      activeDot={{ r: 7, fill: BRAND, stroke: "#fff", strokeWidth: 2 }}
                      filter="url(#shadowLine)"
                    />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </ChartCard>
          </div>

          {/* Gráficos linha 2: Comparação por loja */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
            <ChartCard title="Total de Vendas por Loja" sub={periodLabel} badge={ordersStore.length ? `${ordersStore.length} lojas` : null}>
              {ordersStore.length === 0 ? <EmptyChart /> : (
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={ordersStore} margin={{ top: 8, right: 16, left: 10, bottom: 56 }}>
                    <CartesianGrid stroke="#F3F4F6" strokeDasharray="4 4" vertical={false} />
                    <XAxis dataKey="storeName" interval={0} angle={-35} textAnchor="end" tick={{ fontSize: 11, fill: "#9CA3AF" }} axisLine={false} tickLine={false} />
                    <YAxis width={88} tick={{ fontSize: 11, fill: "#9CA3AF" }} tickFormatter={brlAxis} axisLine={false} tickLine={false} />
                    <Tooltip content={<CustomTooltip formatter={brlTooltip} />} />
                    <Bar dataKey="Total" name="Vendas" fill={BRAND} barSize={20} radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </ChartCard>
            <ChartCard title="Ticket Médio por Loja" sub={periodLabel} badge={avgOrdersStore.length ? `${avgOrdersStore.length} lojas` : null}>
              {avgOrdersStore.length === 0 ? <EmptyChart /> : (
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={avgOrdersStore} margin={{ top: 8, right: 16, left: 10, bottom: 56 }}>
                    <CartesianGrid stroke="#F3F4F6" strokeDasharray="4 4" vertical={false} />
                    <XAxis dataKey="storeName" interval={0} angle={-35} textAnchor="end" tick={{ fontSize: 11, fill: "#9CA3AF" }} axisLine={false} tickLine={false} />
                    <YAxis width={88} tick={{ fontSize: 11, fill: "#9CA3AF" }} tickFormatter={brlAxis} axisLine={false} tickLine={false} />
                    <Tooltip content={<CustomTooltip formatter={brlTooltip} />} />
                    <Bar dataKey="Total" name="Ticket Médio" fill="#3B82F6" barSize={20} radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </ChartCard>
          </div>

          {/* Gráficos linha 3: Por vendedor */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
            <ChartCard title="Vendas por Vendedor(a)" sub={periodLabel} badge={ordersUser.length ? `${ordersUser.length} vendedores` : null}>
              {ordersUser.length === 0 ? <EmptyChart /> : (
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={ordersUser} margin={{ top: 8, right: 16, left: 10, bottom: 56 }}>
                    <CartesianGrid stroke="#F3F4F6" strokeDasharray="4 4" vertical={false} />
                    <XAxis dataKey="managedBy" interval={0} angle={-35} textAnchor="end" tick={{ fontSize: 11, fill: "#9CA3AF" }} axisLine={false} tickLine={false} />
                    <YAxis width={88} tick={{ fontSize: 11, fill: "#9CA3AF" }} tickFormatter={brlAxis} axisLine={false} tickLine={false} />
                    <Tooltip content={<CustomTooltip formatter={brlTooltip} />} />
                    <Bar dataKey="Total" name="Vendas" fill={BRAND} barSize={20} radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </ChartCard>
            <ChartCard title="Venda Média por Vendedor(a)" sub={periodLabel} badge={avgOrdersUser.length ? "ticket médio" : null}>
              {avgOrdersUser.length === 0 ? <EmptyChart /> : (
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={avgOrdersUser} margin={{ top: 8, right: 16, left: 10, bottom: 56 }}>
                    <CartesianGrid stroke="#F3F4F6" strokeDasharray="4 4" vertical={false} />
                    <XAxis dataKey="managedBy" interval={0} angle={-35} textAnchor="end" tick={{ fontSize: 11, fill: "#9CA3AF" }} axisLine={false} tickLine={false} />
                    <YAxis width={88} tick={{ fontSize: 11, fill: "#9CA3AF" }} tickFormatter={brlAxis} axisLine={false} tickLine={false} />
                    <Tooltip content={<CustomTooltip formatter={brlTooltip} />} />
                    <Bar dataKey="Total" name="Média" fill="#10B981" barSize={20} radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </ChartCard>
          </div>

          {/* Gráficos linha 4: Por representante (condicional) */}
          {reps.length > 0 && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
              <ChartCard title="Vendas por Representante" sub={periodLabel} badge={ordersRep.length ? `${ordersRep.length} reps` : null}>
                {ordersRep.length === 0 ? <EmptyChart /> : (
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={ordersRep} margin={{ top: 8, right: 16, left: 10, bottom: 56 }}>
                      <CartesianGrid stroke="#F3F4F6" strokeDasharray="4 4" vertical={false} />
                      <XAxis dataKey="userRepName" interval={0} angle={-35} textAnchor="end" tick={{ fontSize: 11, fill: "#9CA3AF" }} axisLine={false} tickLine={false} />
                      <YAxis width={88} tick={{ fontSize: 11, fill: "#9CA3AF" }} tickFormatter={brlAxis} axisLine={false} tickLine={false} />
                      <Tooltip content={<CustomTooltip formatter={brlTooltip} />} />
                      <Bar dataKey="Total" name="Vendas" fill="#6B7280" barSize={20} radius={[6, 6, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </ChartCard>
              <ChartCard title="Venda Média por Representante" sub={periodLabel}>
                {avgOrdersRep.length === 0 ? <EmptyChart /> : (
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={avgOrdersRep} margin={{ top: 8, right: 16, left: 10, bottom: 56 }}>
                      <CartesianGrid stroke="#F3F4F6" strokeDasharray="4 4" vertical={false} />
                      <XAxis dataKey="userRepName" interval={0} angle={-35} textAnchor="end" tick={{ fontSize: 11, fill: "#9CA3AF" }} axisLine={false} tickLine={false} />
                      <YAxis width={88} tick={{ fontSize: 11, fill: "#9CA3AF" }} tickFormatter={brlAxis} axisLine={false} tickLine={false} />
                      <Tooltip content={<CustomTooltip formatter={brlTooltip} />} />
                      <Bar dataKey="Total" name="Média" fill="#8B5CF6" barSize={20} radius={[6, 6, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </ChartCard>
            </div>
          )}

        </div>
      )}
    </div>
  );
}
